import { Component, AfterViewChecked, Input, ElementRef, ViewChild, OnInit } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { StudentSpeakingScore, UnitSparcScore } from '../data/speaking-score';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-student-sparc-details',
  templateUrl: './student-sparc-details.component.html',
  styleUrls: ['./student-sparc-details.component.scss']
})

export class StudentSparcDetailsComponent extends BaseComponent implements AfterViewChecked, OnInit {
  @Input() speakingScore: StudentSpeakingScore;
  @Input() speakingScoreHistory: UnitSparcScore[];
  @Input() isShowingWida: boolean;

  hasSPARCScore: boolean = false;
  hasSPARCProgressData: boolean = false;
  speakingScoreIncreased: boolean;
  speakingScoreDecreased: boolean; // we need both of these because we don't want to show either icon if the score stayed the same
  @ViewChild('speakingScoreChart') chartElement!: ElementRef<HTMLCanvasElement>;
  chart: Chart;
  chartInitialized: boolean = false;

  ngAfterViewChecked() {
    this.showChart();
  }

  ngOnInit(): void {
    this.hasSPARCScore = true;
    this.displaySpeakingScoreArrow();
    
    this.hasSPARCProgressData = this.speakingScoreHistory && Object.keys(this.speakingScoreHistory).length > 0;
    this.displaySpeakingScoreArrow();
    this.showChart();
  }

  displaySpeakingScoreArrow(): void {
    if (this.speakingScore && this.hasSPARCProgressData && Object.keys(this.speakingScoreHistory).length > 1) {
      // the last element of the array is the current score, so we want to get the one before that
      var lastScore = this.speakingScoreHistory[this.speakingScoreHistory.length - 2]
      if (lastScore.StudentScore < this.speakingScore.Score) {
        this.speakingScoreIncreased = true;
      }
      else if (lastScore.StudentScore > this.speakingScore.Score) {
        this.speakingScoreDecreased = true;
      }
      // otherwise leave both as false and don't display an icon
    }
  }

  showChart() {
    if (this.chartInitialized || !this.chartElement || !this.hasSPARCProgressData) return;

    const unitNumbers = Array.from({ length: 24 }, (_, i) => (i + 1).toString());

    var studentScoresArray = new Array(24).fill(null);
    this.speakingScoreHistory.forEach(scoreData => studentScoresArray[scoreData.UnitNumber - 1] = scoreData.StudentScore);

    var averageScoresArray = new Array(24).fill(null);
    this.speakingScoreHistory.forEach(scoreData => averageScoresArray[scoreData.UnitNumber - 1] = scoreData.ReferenceAverageScore);

    const studentScores = this.speakingScoreHistory.map(scoreData => scoreData.StudentScore)
    const avgScores = this.speakingScoreHistory.map(scoreData => scoreData.ReferenceAverageScore)

    const minOverallScore = Math.min(Math.min.apply(Math, studentScores), Math.min.apply(Math, avgScores));
    const maxOverallScore = Math.max(Math.max.apply(Math, studentScores), Math.max.apply(Math, avgScores));

    // adjust scale based on the difference between min and max scores
    const yAxisStep = (maxOverallScore - minOverallScore > 100) ? 100 : 50;
    const yAxisMin = Math.floor(minOverallScore / yAxisStep) * yAxisStep;
    const yAxisMax = Math.ceil(maxOverallScore / yAxisStep) * yAxisStep;

    this.chart = new Chart(this.chartElement.nativeElement, {
      type: 'line',
      data: {
        labels: unitNumbers,
        datasets: [
          {
            label: 'Score',
            data: studentScoresArray,
            borderColor: '#03B1F2',
            backgroundColor: '#03B1F2',
            borderWidth: 1,
            pointRadius: 2,
            order: 1
          },
          {
            label: 'Average',
            data: avgScores,
            borderColor: '#9b9b9b',
            backgroundColor: '#9b9b9b26',
            borderWidth: 1,
            pointRadius: 1,
            order: 2,
            borderDash: [3, 3],
            fill: true
          },
        ],
      },
      options: {
        clip: 0,
        aspectRatio: 4,
        plugins: {
          title: {
            display: true,
            text: 'SPARC over time (by unit)',
            color: '#03B1F2',
            font: {
              family: 'Lato',
              weight: 600,
              size: 15
            },
            padding: {
              top: 0,
              bottom: 8
            }
          },
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'white',
            borderColor: (tooltipItem) => {
              if (tooltipItem.tooltipItems[0] && tooltipItem.tooltipItems[0].datasetIndex === 1) {
                return '#b3b3b3';
              }
              return '#03b1f2'
            },
            borderWidth: 1,
            caretPadding: 10,
            bodyAlign: 'center',
            bodyFont: {
              family: 'Lato',
              size: 16,
              weight: 600
            },
            bodyColor: '#212529',
            footerAlign: 'center',
            footerFont: {
              family: 'Lato',
              size: 12,
              weight: 200
            },
            footerColor: '#212529',
            footerMarginTop: 2,
            displayColors: false,
            padding: 6,
            callbacks: {
              title: function (context) { return ''; },
              label: function (context) {
                return context.formattedValue;
              },
              footer: function (context) {
                var text = ''
                if (context[0].datasetIndex === 1) {
                  text = 'Global average\nfor ';
                }
                return text + 'Unit ' + context[0].label;
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                family: 'Lato',
                size: 10
              },
              minRotation: 0,
              maxRotation: 0
            }
          },
          y: {
            min: yAxisMin,
            max: yAxisMax,
            ticks: {
              font: {
                family: 'Lato',
                size: 10
              }
            }
          }
        },
      },
    });
    this.chartInitialized = true;
  }
}